import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import Table from "../shared/Table";
import { useHistory } from "react-router-dom";
import EditIcon from "@material-ui/icons/Edit";
import { useAppContext } from "../../api/AppContext";

const EMPLOYEE_REGISTRY_FILTERS_SESSION_KEY = "employeeRegistry.filters.v1";

const EmployeeRegistry = () => {
  const history = useHistory();
  const {
    employeeRegistryTenants,
    isEmployeeRegistryTenantsLoading,
    fetchEmployeeRegistryTenants,

    employeeRegistryEmployees,
    fetchEmployeeRegistryEmployees,
    employeeRegistryValues,
    isEmployeeRegistryValuesLoading,
    fetchEmployeeRegistryValues,
  } = useAppContext();

  const [selectedTenant, setSelectedTenant] = useState("");
  const [wecareFilter, setWecareFilter] = useState("all");
  const [dataQualityFilter, setDataQualityFilter] = useState("all");
  const [wecareOpen, setWecareOpen] = useState(false);
  const [dataQualityOpen, setDataQualityOpen] = useState(false);

  const [error, setError] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isRowsLoading, setIsRowsLoading] = useState(false);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [tableResetKey, setTableResetKey] = useState(0);
  const latestRowsRequestRef = useRef(0);
  const expectedValuesByFieldRef = useRef({
    contracttype: new Set(),
    employeerole: new Set(),
    employeetype: new Set(),
    entity: new Set(),
    officelocation: new Set(),
    officetype: null,
  });
  const initialStoredTenantRef = useRef("");
  const hasInitializedTenantFromSessionRef = useRef(false);
  const [isSessionFiltersHydrated, setIsSessionFiltersHydrated] =
    useState(false);

  const persistFiltersToSession = useCallback(
    (nextFilters) => {
      if (!isSessionFiltersHydrated) return;
      try {
        sessionStorage.setItem(
          EMPLOYEE_REGISTRY_FILTERS_SESSION_KEY,
          JSON.stringify(nextFilters)
        );
      } catch (_error) {
        // Ignore write failures (e.g. storage disabled).
      }
    },
    [isSessionFiltersHydrated]
  );

  const reloadPage = useCallback(() => {
    // Keep it async so the Select can close cleanly before reload.
    setTimeout(() => window.location.reload(), 0);
  }, []);

  useEffect(() => {
    try {
      const rawFilters = sessionStorage.getItem(
        EMPLOYEE_REGISTRY_FILTERS_SESSION_KEY
      );
      if (!rawFilters) return;

      const parsedFilters = JSON.parse(rawFilters);
      initialStoredTenantRef.current = parsedFilters?.selectedTenant || "";
      if (parsedFilters?.wecareFilter) {
        setWecareFilter(parsedFilters.wecareFilter);
      }
      if (parsedFilters?.dataQualityFilter) {
        setDataQualityFilter(parsedFilters.dataQualityFilter);
      }
    } catch (_error) {
      // Ignore invalid or unavailable session storage payloads.
    } finally {
      setIsSessionFiltersHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!isSessionFiltersHydrated) return;
    try {
      sessionStorage.setItem(
        EMPLOYEE_REGISTRY_FILTERS_SESSION_KEY,
        JSON.stringify({
          dataQualityFilter,
          selectedTenant,
          wecareFilter,
        })
      );
    } catch (_error) {
      // Ignore write failures (e.g. storage disabled).
    }
  }, [
    selectedTenant,
    wecareFilter,
    dataQualityFilter,
    isSessionFiltersHydrated,
  ]);

  useEffect(() => {
    fetchEmployeeRegistryTenants();
  }, [fetchEmployeeRegistryTenants]);

  useEffect(() => {
    fetchEmployeeRegistryValues();
  }, [fetchEmployeeRegistryValues]);

  const tenants = useMemo(
    () =>
      (employeeRegistryTenants || []).map((c) => ({
        id: c.id,
        name: c.name,
        accessType: c.readonly ? "read" : "write",
      })),
    [employeeRegistryTenants]
  );

  useEffect(() => {
    if (!tenants || tenants.length === 0) return;
    if (!isSessionFiltersHydrated) return;

    if (!hasInitializedTenantFromSessionRef.current) {
      hasInitializedTenantFromSessionRef.current = true;
      const storedTenant = initialStoredTenantRef.current;
      const isStoredTenantValid = tenants.some((c) => c.id === storedTenant);
      if (isStoredTenantValid) {
        if (selectedTenant !== storedTenant) {
          setSelectedTenant(storedTenant);
        }
        return;
      }
    }

    const stillValid = tenants.some((c) => c.id === selectedTenant);
    if (!selectedTenant || !stillValid) {
      setSelectedTenant(tenants[0].id);
    }
  }, [tenants, selectedTenant, isSessionFiltersHydrated]);

  const loadRows = useCallback(
    async (tenantId) => {
      const requestId = latestRowsRequestRef.current + 1;
      latestRowsRequestRef.current = requestId;
      try {
        setError(null);
        setFilteredEmployees([]);
        setIsRowsLoading(true);
        const fetchPromise = fetchEmployeeRegistryEmployees(tenantId || "all");
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error("Employees request timeout"));
          }, 20000);
        });
        await Promise.race([fetchPromise, timeoutPromise]);
      } catch (e) {
        setError(e?.message || "Failed to load employees");
      } finally {
        if (latestRowsRequestRef.current === requestId) {
          setIsRowsLoading(false);
        }
      }
    },
    [fetchEmployeeRegistryEmployees]
  );

  useEffect(() => {
    // Avoid triggering an initial fetch with "all" when no tenant is selected yet.
    if (!selectedTenant) return;
    loadRows(selectedTenant);
  }, [selectedTenant, loadRows]);

  useEffect(() => {
    // Reset the table UI state (page/search/sort) and discard rows when the tenant changes,
    // so it feels like a brand-new page load.
    if (!selectedTenant) return;
    setFilteredEmployees([]);
    setTableResetKey((key) => key + 1);
  }, [selectedTenant]);

  const canWrite = useMemo(() => {
    const current = tenants.find((c) => c.id === selectedTenant);
    return current?.accessType === "write";
  }, [tenants, selectedTenant]);

  const expectedValuesByField = useMemo(() => {
    if (
      isEmployeeRegistryValuesLoading ||
      !employeeRegistryValues.entity?.length
    ) {
      return expectedValuesByFieldRef.current;
    }

    expectedValuesByFieldRef.current = {
      contracttype: new Set(
        (employeeRegistryValues?.contracttype || []).map((opt) => opt?.value)
      ),
      employeerole: new Set(
        (employeeRegistryValues?.employeerole || []).map((opt) => opt?.value)
      ),
      employeetype: new Set(
        (employeeRegistryValues?.employeetype || []).map((opt) => opt?.value)
      ),
      entity: new Set(
        (employeeRegistryValues?.entity || []).map((opt) => opt?.value)
      ),
      officelocation: new Set(
        (employeeRegistryValues?.officelocation || []).map((opt) => opt?.value)
      ),
      // Office Type is treated as free text: only non-empty is required.
      officetype: null,
    };
    return expectedValuesByFieldRef.current;
  }, [isEmployeeRegistryValuesLoading, employeeRegistryValues]);

  const isFieldInvalid = useCallback(
    (employee, field) => {
      const rawValue = employee?.[field];
      const value = typeof rawValue === "string" ? rawValue.trim() : rawValue;

      if (!value) return true;
      if (field === "officetype") return false;

      const allowedValues = expectedValuesByField[field];
      if (!allowedValues) return false;
      if (allowedValues.size === 0) return false;
      return !allowedValues.has(value);
    },
    [expectedValuesByField]
  );

  const dataQualityFields = useMemo(
    () => Object.keys(expectedValuesByField),
    [expectedValuesByField]
  );

  useEffect(() => {
    let cancelled = false;

    // Yield to the browser so UI updates (e.g. Select close + loader render)
    // happen before the expensive filtering work.
    setFilteredEmployees([]);
    setIsFiltering(true);
    const timer = setTimeout(() => {
      try {
        if (cancelled) return;
        if (!Array.isArray(employeeRegistryEmployees)) {
          setFilteredEmployees([]);
          return;
        }

        const hasIncompleteOrWrongData = (employee) => {
          for (let i = 0; i < dataQualityFields.length; i += 1) {
            if (isFieldInvalid(employee, dataQualityFields[i])) return true;
          }
          return false;
        };

        const next = employeeRegistryEmployees.filter((employee) => {
          if (
            wecareFilter === "enabled" &&
            employee?.haswecareenabled !== true
          ) {
            return false;
          }
          if (
            dataQualityFilter === "incomplete_or_wrong" &&
            !hasIncompleteOrWrongData(employee)
          ) {
            return false;
          }
          return true;
        });

        if (!cancelled) setFilteredEmployees(next);
      } finally {
        if (!cancelled) setIsFiltering(false);
      }
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    employeeRegistryEmployees,
    wecareFilter,
    dataQualityFilter,
    expectedValuesByField,
    isFieldInvalid,
    dataQualityFields,
  ]);

  const columns = useMemo(() => {
    const getInvalidCellStyle = (field) => (_cellValue, rowData) =>
      isFieldInvalid(rowData, field) ? { color: "#d32f2f" } : {};

    const base = [
      { title: "Email", field: "mail", width: 240 },
      {
        title: "Employee Role",
        field: "employeerole",
        width: 150,
        cellStyle: getInvalidCellStyle("employeerole"),
      },
      {
        title: "Employee Type",
        field: "employeetype",
        width: 150,
        cellStyle: getInvalidCellStyle("employeetype"),
      },
      {
        title: "Entity",
        field: "entity",
        width: 120,
        cellStyle: getInvalidCellStyle("entity"),
      },
      {
        title: "Contract Type",
        field: "contracttype",
        width: 150,
        cellStyle: getInvalidCellStyle("contracttype"),
      },
      {
        title: "Office Location",
        field: "officelocation",
        width: 150,
        cellStyle: getInvalidCellStyle("officelocation"),
      },
      {
        title: "Office Type",
        field: "officetype",
        width: 140,
        cellStyle: getInvalidCellStyle("officetype"),
      },
      { title: "User Type", field: "usertype", width: 140 },
      {
        title: "WeCare Installed",
        field: "haswecareenabled",
        type: "boolean",
        width: 140,
      },
    ];

    if (canWrite) {
      base.push({
        title: "Actions",
        field: "edit",
        width: 25,
        sorting: false,
        render: (row) => (
          <Tooltip title="Edit">
            <IconButton
              aria-label="edit"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                history.push(
                  `/welfares/employee-registry/${
                    row.id
                  }?tenantid=${encodeURIComponent(selectedTenant)}`
                );
              }}
            >
              <EditIcon style={{ color: "#0000008a" }} />
            </IconButton>
          </Tooltip>
        ),
      });
    }

    return base;
  }, [history, canWrite, selectedTenant, isFieldInvalid]);

  return (
    <Box
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      height="100%"
      p={4}
      width="100%"
    >
      <Grid container direction="column" spacing={4}>
        <Grid item>
          <Grid container alignItems="center" spacing={1}>
            <Grid item md={1} sm="auto" xs={12}>
              <FormControl
                fullWidth
                size="small"
                style={{ maxWidth: 360 }}
                variant="outlined"
              >
                <InputLabel id="tenant-select-label">Tenant</InputLabel>
                <Select
                  disabled={isEmployeeRegistryTenantsLoading}
                  label="Tenant"
                  labelId="tenant-select-label"
                  value={selectedTenant}
                  onChange={(e) => {
                    const nextTenant = e.target.value;
                    persistFiltersToSession({
                      dataQualityFilter,
                      selectedTenant: nextTenant,
                      wecareFilter,
                    });
                    setSelectedTenant(nextTenant);
                    reloadPage();
                  }}
                >
                  {tenants.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item md={1} sm="auto" xs={12}>
              <FormControl
                fullWidth
                size="small"
                style={{ maxWidth: 220 }}
                variant="outlined"
              >
                <InputLabel id="wecare-filter-label">WeCare</InputLabel>
                <Select
                  label="WeCare"
                  labelId="wecare-filter-label"
                  open={wecareOpen}
                  value={wecareFilter}
                  onChange={(e) => {
                    const nextWecare = e.target.value;
                    setWecareOpen(false);
                    persistFiltersToSession({
                      dataQualityFilter,
                      selectedTenant,
                      wecareFilter: nextWecare,
                    });
                    setWecareFilter(nextWecare);
                    reloadPage();
                  }}
                  onClose={() => setWecareOpen(false)}
                  onOpen={() => setWecareOpen(true)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="enabled">Installed only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item md={1} sm="auto" xs={12}>
              <FormControl
                fullWidth
                size="small"
                style={{ maxWidth: 280 }}
                variant="outlined"
              >
                <InputLabel id="data-quality-filter-label">
                  Data Quality
                </InputLabel>
                <Select
                  disabled={isEmployeeRegistryValuesLoading}
                  label="Data Quality"
                  labelId="data-quality-filter-label"
                  open={dataQualityOpen}
                  value={dataQualityFilter}
                  onChange={(e) => {
                    const nextQuality = e.target.value;
                    setDataQualityOpen(false);
                    persistFiltersToSession({
                      dataQualityFilter: nextQuality,
                      selectedTenant,
                      wecareFilter,
                    });
                    setDataQualityFilter(nextQuality);
                    reloadPage();
                  }}
                  onClose={() => setDataQualityOpen(false)}
                  onOpen={() => setDataQualityOpen(true)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="incomplete_or_wrong">
                    Incomplete / wrong data
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {isFiltering && (
              <Grid item>
                <CircularProgress size={20} />
              </Grid>
            )}
            {isEmployeeRegistryTenantsLoading && (
              <Grid item>
                <CircularProgress size={20} />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item style={{ flex: 1, minHeight: 0, minWidth: 0 }}>
          {error && (
            <Typography gutterBottom color="error" variant="body2">
              {error}
            </Typography>
          )}
          <Box
            style={{
              width: "100%",
              overflowX: "auto", // scroll solo orizzontale
              overflowY: "visible", // nessuna scrollbar verticale qui
              minWidth: 0,
            }}
          >
            <Table
              key={tableResetKey}
              columns={columns}
              data={filteredEmployees}
              isLoading={isRowsLoading || isFiltering}
              options={{
                pageSize: 10,
                pageSizeOptions: [5, 10, 15],
                search: true,
                filtering: false,
                headerStyle: { whiteSpace: "nowrap" },
                cellStyle: { whiteSpace: "nowrap" },
              }}
              refetch={() => loadRows(selectedTenant)}
              tableActions={[]}
              title="Employees"
              onRowClick={
                canWrite
                  ? (_evt, row) =>
                      history.push(
                        `/welfares/employee-registry/${
                          row.id
                        }?tenantid=${encodeURIComponent(selectedTenant)}`
                      )
                  : undefined
              }
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmployeeRegistry;
